import React, { useState, useEffect, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import { useComponentTranslation } from "@/services/i18n";
import { countryLabel } from "@/constants/countries";
import { FiFilter, FiSearch, FiDownload, FiChevronLeft, FiChevronRight, FiMail, FiLinkedin, FiGithub, FiGlobe, FiPhone, FiFileText, FiPlus } from "react-icons/fi";
import toast from "react-hot-toast";

import Loader from "@/components/loader";
import CandidateModal from "@/components/candidate-modal";
import ImportCandidateModal from "@/components/import-candidate-modal";
import Modal from "@/components/modal";
import LoadingButton from "@/components/loading-button";
import FilterCountry from "./components/FilterCountry";
import FilterCity from "./components/FilterCity";
import FilterSkills from "./components/FilterSkills";
import FilterUniversity from "./components/FilterUniversity";
import FilterExperiences from "./components/FilterExperiences";
import FilterLanguage from "./components/FilterLanguage";

import api from "@/services/api";
import useStore from "@/services/store";
import { downloadResume } from "@/utils";
import { capture } from "@/services/sentry";
import { Menu, MenuItem, MenuItems, Transition } from "@headlessui/react";
import { FaEllipsisV } from "react-icons/fa";

const STATUSES = ["APPLIED", "PRESELECTED", "INTERVIEW", "SELECTED", "REFUSED"];

export default function Pool() {
  const { t } = useComponentTranslation(translations);
  const { user, organization } = useStore();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [candidatesToMessage, setCandidatesToMessage] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedCandidates, setSelectedCandidates] = useState(new Set());
  const [selectAllPages, setSelectAllPages] = useState(false);
  const [selectedCandidateUser, setSelectedCandidateUser] = useState(null);
  const [filters, setFilters] = useState({
    page: parseInt(searchParams.get("page")) || 1,
    limit: parseInt(searchParams.get("limit")) || 100,
    search: searchParams.get("search") || "",
    countries: { include: [], exclude: [] },
    cities: { include: [], exclude: [] },
    skills: { include: [], exclude: [] },
    universities: { include: [], exclude: [] },
    job_titles: { include: [], exclude: [] },
    languages: { include: [], exclude: [] },
  });

  useEffect(() => {
    fetchCandidates();
  }, [filters, organization?._id]);

  // Handle user_id parameter to open modal automatically
  useEffect(() => {
    const userId = searchParams.get("user_id");
    if (userId && !selectedCandidateUser) {
      fetchCandidateByUserId(userId);
    }
  }, [searchParams]);

  const fetchCandidates = async () => {
    try {
      setLoading(true);
      const params = {
        organization_id: organization?._id, organization_name: organization?.name,
        page: filters.page,
        limit: filters.limit,
        status: STATUSES,
        countries: filters.countries,
        cities: filters.cities,
        skills: filters.skills,
        universities: filters.universities,
        job_titles: filters.job_titles,
        languages: filters.languages,
      };

      if (filters.search) params.search = filters.search;

      // Update URL params
      const urlParams = new URLSearchParams();
      urlParams.set("page", filters.page);
      urlParams.set("limit", filters.limit);
      if (filters.search) urlParams.set("search", filters.search);
      setSearchParams(urlParams);

      params.role_candidate = true;
      params.organization_id = organization?._id;
      const { data, ok, total } = await api.post("/user/search", params);

      if (!ok) {
        toast.error(t("fetch_error"));
        return;
      }

      setCandidates(data);
      setTotal(total);
    } catch (e) {
      console.error(e);
      capture(e);
      toast.error(t("fetch_error"));
    } finally {
      setLoading(false);
    }
  };

  const fetchJobs = async () => {
    // try {
    //   const { ok, data } = await api.post("/job/search", {
    //     organization_id: organization?._id, organization_name: organization?.name,
    //     limit: 1000,
    //   });
    //
    //   if (ok) {
    //     setJobs(data || []);
    //   }
    // } catch (e) {
    //   console.error(e);
    //   capture(e);
    // }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: 1, // Reset to first page when filters change
    }));
    setSelectedCandidates(new Set()); // Clear selection when filters change
    setSelectAllPages(false);
  };

  const handlePageChange = (page) => {
    setFilters((prev) => ({ ...prev, page }));
    if (!selectAllPages) {
      setSelectedCandidates(new Set()); // Clear selection when changing page (unless all pages selected)
    }
  };

  const handleSelectAll = () => {
    if (selectedCandidates.size === candidates.length && !selectAllPages) {
      setSelectedCandidates(new Set());
    } else {
      setSelectedCandidates(new Set(candidates.map((c) => c._id)));
      setSelectAllPages(false);
    }
  };

  const handleSelectAllPages = () => {
    setSelectAllPages(true);
    // Note: In a real implementation with full data, you'd get all candidate IDs
    // For now, we mark that all pages are selected with the flag
  };

  const handleToggleCandidate = (candidateId) => {
    const newSelected = new Set(selectedCandidates);
    if (newSelected.has(candidateId)) {
      newSelected.delete(candidateId);
    } else {
      newSelected.add(candidateId);
    }
    setSelectedCandidates(newSelected);
    setSelectAllPages(false); // Unselect "all pages" if manually toggling
  };

  const handleClearSelection = () => {
    setSelectedCandidates(new Set());
    setSelectAllPages(false);
  };

  const handleSelectCandidateForModal = async (candidate) => {
    try {
      const { ok, data } = await api.get(`/user/${candidate._id}`);
      if (ok) {
        setSelectedCandidateUser(data);
      }
    } catch (e) {
      console.error(e);
      toast.error("Error fetching candidate details");
    }
  };

  const fetchCandidateByUserId = async (userId) => {
    try {
      const { ok, data } = await api.get(`/user/${userId}`);
      if (ok) {
        setSelectedCandidateUser(data);
      } else {
        toast.error(t("error_fetching_candidate"));
      }
    } catch (e) {
      console.error(e);
      capture(e);
      toast.error(t("error_fetching_candidate"));
    }
  };

  const handleExport = async () => {
    try {
      let selectedCandidatesData;

      // If all pages selected, fetch all candidates from API
      if (selectAllPages) {
        const params = {
          organization_id: organization?._id, organization_name: organization?.name,
          page: 1,
          limit: total, // Fetch all candidates
          status: STATUSES,
          countries: filters.countries,
          cities: filters.cities,
          skills: filters.skills,
          universities: filters.universities,
          job_titles: filters.job_titles,
          languages: filters.languages,
        };

        params.selectAll = true;

        if (filters.search) params.search = filters.search;

        params.role_candidate = true;
        params.organization_id = organization?._id;

        const { data, ok } = await api.post("/user/search", params);

        if (!ok) {
          toast.error(t("fetch_error"));
          return;
        }

        selectedCandidatesData = data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      } else {
        // Export only selected candidates from current page
        selectedCandidatesData = candidates.filter((c) => selectedCandidates.has(c._id));
      }

      // Generate CSV
      const csvContent = [
        ["Name", "Email", "Phone", "City", "Country", "Last Company", "Last School", "Applied Date", "Resume URL"],
        ...selectedCandidatesData.map((c) => [
          `"${c.firstname} ${c.lastname}"`,
          c.email || "-",
          c.phone || "-",
          `"${c.city || "-"}"`,
          `"${countryLabel(c.country) || "-"}"`,
          `"${c.experiences?.[0]?.company || "-"}"`,
          `"${c.education?.[0]?.institution || "-"}"`,
          c.created_at ? new Date(c.created_at).toLocaleDateString() : "-",
          c.resume_url || "-",
        ]),
      ]
        .map((row) => row.join(","))
        .join("\n");

      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `candidates_export_${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(t("export_success"));
    } catch (e) {
      console.error(e);
      capture(e);
      toast.error(t("export_error"));
    }
  };

  const allCurrentPageSelected = candidates.length > 0 && selectedCandidates.size === candidates.length;
  const totalPages = Math.ceil(total / filters.limit);

  return (
    <div className="w-full p-6">
      {/* Search and Filters Row */}
      <div className="mb-4 flex items-center gap-3">
        {/* Search */}
        <div className="flex-1 relative">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder={t("search_placeholder")}
            value={filters.search}
            onChange={(e) => handleFilterChange("search", e.target.value)}
            className="input w-full pl-10"
          />
        </div>

        {/* More Filters Button */}
        <button onClick={() => setShowMoreFilters(!showMoreFilters)} className={`btn flex items-center gap-2 ${showMoreFilters ? "btn-primary" : "btn-secondary"}`}>
          <FiFilter className="w-4 h-4" />
          {t("more_filters")}
        </button>

        <Menu as="div" className="relative inline-block text-left">
          <Menu.Button className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            <FaEllipsisV className="h-5 w-5" />
          </Menu.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <MenuItems className="absolute right-0 mt-2 w-48 origin-top-right bg-white rounded-lg shadow-lg z-50 border border-gray-200 focus:outline-none">
              <div className="py-1">
                <MenuItem>
                  {({ active }) => (
                    <button
                      className={`${active ? "bg-gray-100" : ""} w-full text-left px-4 py-2 text-sm text-gray-700 flex items-center gap-2`}
                      onClick={() => setShowImportModal(true)}
                    >
                      <FiPlus className="h-4 w-4" />
                      <span>{t("import_candidate")}</span>
                    </button>
                  )}
                </MenuItem>
              </div>
            </MenuItems>
          </Transition>
        </Menu>
      </div>

      {/* Expanded Filters Section */}
      {showMoreFilters && (
        <div className="mb-4 p-4 bg-gray-50 rounded-xl border border-gray-200 flex flex-wrap gap-4">
          <FilterCountry filters={filters} setFilters={setFilters} />
          <FilterCity filters={filters} setFilters={setFilters} />
          <FilterExperiences filters={filters} setFilters={setFilters} />
          <FilterSkills filters={filters} setFilters={setFilters} />
          <FilterUniversity filters={filters} setFilters={setFilters} />
          <FilterLanguage filters={filters} setFilters={setFilters} />
        </div>
      )}

      {/* Total Count / Bulk Selection Bar with Pagination */}
      <div className={`mb-1 flex items-center justify-between ${selectedCandidates.size > 0 ? "bg-[#6C4CFF] rounded-xl py-2.5 px-4" : "py-1"}`}>
        {/* Left side: Total or Bulk Actions */}
        {selectedCandidates.size > 0 ? (
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-white font-semibold text-base">
                {selectAllPages ? `${t("all_candidates_selected").replace("{count}", total)}` : `${selectedCandidates.size} ${t("selected")}`}
              </span>
              {!selectAllPages && selectedCandidates.size === candidates.length && total > candidates.length && (
                <>
                  <span className="text-white/60">|</span>
                  <button onClick={handleSelectAllPages} className="text-white underline text-sm hover:no-underline">
                    {t("select_all_candidates").replace("{count}", total)}
                  </button>
                </>
              )}
            </div>
            <button
              onClick={async () => {
                // Get selected candidates data
                let selectedCandidatesData;
                if (selectAllPages) {
                  // Fetch all candidates
                  const params = {
                    organization_id: organization?._id, organization_name: organization?.name,
                    page: 1,
                    limit: total,
                    status: STATUSES,
                    countries: filters.countries,
                    cities: filters.cities,
                    skills: filters.skills,
                    universities: filters.universities,
                    job_titles: filters.job_titles,
                    languages: filters.languages,
                  };
                  params.selectAll = true;
                  if (filters.search) params.search = filters.search;
                  params.role_candidate = true;
                  const { data, ok } = await api.post("/user/search", params);
                  if (!ok) {
                    toast.error(t("fetch_error"));
                    return;
                  }
                  selectedCandidatesData = data;
                } else {
                  selectedCandidatesData = candidates.filter((c) => selectedCandidates.has(c._id));
                }
                setCandidatesToMessage(selectedCandidatesData);
                setShowMessageModal(true);
              }}
              className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 text-sm font-medium transition-colors flex items-center gap-2"
            >
              <FiMail className="w-3.5 h-3.5" />
              {t("message_candidate")}
            </button>
            <button
              onClick={handleExport}
              className="bg-white text-gray-900 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors flex items-center gap-2"
            >
              <FiDownload className="w-3.5 h-3.5" />
              {t("export")}
            </button>
            <button onClick={handleClearSelection} className="text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-white/10 transition-colors">
              {t("clear")}
            </button>
          </div>
        ) : (
          <div className="text-sm font-medium text-gray-700">
            {t("total_candidates")} <span className="font-bold">{total}</span>
          </div>
        )}

        {/* Right side: Pagination */}
        {candidates.length > 0 && (
          <div className="flex items-center gap-3">
            <button
              onClick={() => handlePageChange(filters.page - 1)}
              disabled={filters.page === 1}
              className={`p-1 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                selectedCandidates.size > 0 ? "text-white hover:bg-white/10" : "text-gray-600"
              }`}
            >
              <FiChevronLeft className="w-4 h-4" />
            </button>
            <span className={`text-xs ${selectedCandidates.size > 0 ? "text-white" : "text-gray-600"}`}>
              Page {filters.page} of {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(filters.page + 1)}
              disabled={filters.page >= totalPages}
              className={`p-1 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                selectedCandidates.size > 0 ? "text-white hover:bg-white/10" : "text-gray-600"
              }`}
            >
              <FiChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Candidates Table */}
      {loading ? (
        <Loader />
      ) : candidates.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{t("no_candidates")}</h3>
          <p className="text-sm text-gray-500">{t("no_candidates_description")}</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-2 text-left w-12">
                    <input
                      type="checkbox"
                      checked={allCurrentPageSelected}
                      onChange={handleSelectAll}
                      className="w-4 h-4 rounded border-gray-300 text-[#6C4CFF] focus:ring-[#6C4CFF] cursor-pointer"
                    />
                  </th>
                  <th scope="col" className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("candidate_name")}
                  </th>
                  <th scope="col" className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("city")}
                  </th>
                  <th scope="col" className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("country")}
                  </th>
                  <th scope="col" className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("last_company")}
                  </th>
                  <th scope="col" className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("last_school")}
                  </th>
                  <th scope="col" className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("social")}
                  </th>
                  <th scope="col" className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("applied_date")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {candidates.map((candidate) => (
                  <CandidateRow
                    key={candidate._id}
                    candidate={candidate}
                    t={t}
                    isSelected={selectedCandidates.has(candidate._id)}
                    onToggleSelect={handleToggleCandidate}
                    onSelectCandidate={handleSelectCandidateForModal}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination at bottom */}
      {candidates.length > 0 && (
        <div className="mt-4 flex justify-end">
          <div className="flex items-center gap-3">
            <button
              onClick={() => handlePageChange(filters.page - 1)}
              disabled={filters.page === 1}
              className="p-1 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-gray-600"
            >
              <FiChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-xs text-gray-600">
              Page {filters.page} of {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(filters.page + 1)}
              disabled={filters.page >= totalPages}
              className="p-1 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-gray-600"
            >
              <FiChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Candidate Modal */}
      {selectedCandidateUser && (
        <CandidateModal
          candidate={selectedCandidateUser}
          setCandidate={(candidate) => {
            setSelectedCandidateUser(candidate);
            // Remove user_id from URL when modal is closed
            if (!candidate) {
              const params = new URLSearchParams(searchParams);
              params.delete("user_id");
              setSearchParams(params);
            }
          }}
        />
      )}

      {/* Import Candidate Modal */}
      <ImportCandidateModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSuccess={() => {
          fetchCandidates();
        }}
      />

      {/* Message Candidate Modal */}
      <MessageCandidateModal isOpen={showMessageModal} onClose={() => setShowMessageModal(false)} candidates={candidatesToMessage} t={t} />
    </div>
  );
}

const CandidateRow = ({ candidate, t, isSelected, onToggleSelect, onSelectCandidate }) => {
  const handleCandidateClick = (e) => {
    // Don't open modal if clicking on checkbox
    if (e.target.type === "checkbox") {
      return;
    }
    onSelectCandidate(candidate);
  };

  const handleCheckboxClick = (e) => {
    e.stopPropagation();
    onToggleSelect(candidate._id);
  };

  return (
    <tr onClick={handleCandidateClick} className={`cursor-pointer transition-colors ${isSelected ? "bg-purple-50" : "hover:bg-gray-50"}`}>
      <td className="px-6 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" checked={isSelected} onChange={handleCheckboxClick} className="w-4 h-4 rounded border-gray-300 text-[#6C4CFF] focus:ring-[#6C4CFF] cursor-pointer" />
      </td>
      <td className="px-6 py-3 whitespace-nowrap">
        <div className="flex items-center">
          <div>
            <div className="text-sm font-medium text-gray-900">{`${candidate.firstname} ${candidate.lastname}`}</div>
          </div>
        </div>
      </td>
      <td className="px-6 py-3">
        <div className="text-sm text-gray-500">{candidate.city || "-"}</div>
      </td>
      <td className="px-6 py-3">
        <div className="text-sm text-gray-500">{countryLabel(candidate.country) || "-"}</div>
      </td>
      <td className="px-6 py-3 max-w-xs">
        <div className="text-sm text-gray-500 truncate" title={candidate.experiences?.[0]?.company}>
          {candidate.experiences?.[0]?.company || "-"}
        </div>
      </td>
      <td className="px-6 py-3 max-w-xs">
        <div className="text-sm text-gray-500 truncate" title={candidate.education?.[0]?.institution}>
          {candidate.education?.[0]?.institution || "-"}
        </div>
      </td>
      <td className="px-6 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-1.5">
          {candidate.email && (
            <a href={`mailto:${candidate.email}`} className="p-1.5 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors" title={candidate.email}>
              <FiMail className="w-3.5 h-3.5" />
            </a>
          )}
          {candidate.phone && (
            <a href={`tel:${candidate.phone}`} className="p-1.5 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors" title={candidate.phone}>
              <FiPhone className="w-3.5 h-3.5" />
            </a>
          )}
          {candidate.linkedin && (
            <a
              href={candidate.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1.5 rounded-md bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors"
              title="LinkedIn"
            >
              <FiLinkedin className="w-3.5 h-3.5" />
            </a>
          )}
          {candidate.github && (
            <a
              href={candidate.github}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1.5 rounded-md bg-purple-100 text-purple-600 hover:bg-purple-200 transition-colors"
              title="GitHub"
            >
              <FiGithub className="w-3.5 h-3.5" />
            </a>
          )}
          {candidate.portfolio && (
            <a
              href={candidate.portfolio}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1.5 rounded-md bg-green-100 text-green-600 hover:bg-green-200 transition-colors"
              title="Portfolio"
            >
              <FiGlobe className="w-3.5 h-3.5" />
            </a>
          )}
          {candidate.resume_url && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                downloadResume(candidate);
              }}
              className="p-1.5 rounded-md bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
              title="Resume"
            >
              <FiFileText className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </td>
      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">{candidate.created_at ? new Date(candidate.created_at).toLocaleDateString("fr-FR") : "-"}</td>
    </tr>
  );
};

function MessageCandidateModal({ isOpen, onClose, candidates = [], t }) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSubject("");
      setMessage("");
    }
  }, [isOpen]);

  const handleSend = async () => {
    if (!subject.trim()) {
      toast.error(t("subject_required"));
      return;
    }
    if (!message.trim()) {
      toast.error(t("message_required"));
      return;
    }

    try {
      setSending(true);
      let successCount = 0;
      let failedCount = 0;

      // Loop through all candidates and send messages
      for (const candidate of candidates) {
        try {
          const { ok } = await api.post("/user/message_candidate", {
            user_id: candidate._id,
            subject: subject.trim(),
            message: message.trim(),
            organization_id: organization?._id, organization_name: organization?.name,
          });

          if (ok) {
            successCount++;
          } else {
            failedCount++;
          }
        } catch (e) {
          console.error(e);
          failedCount++;
        }
      }

      if (successCount > 0) {
        if (candidates.length === 1) {
          toast.success(t("message_sent"));
        } else {
          toast.success(t("messages_sent").replace("{count}", successCount));
        }
      }

      if (failedCount > 0) {
        toast.error(t("error_sending_message") + ` (${failedCount} failed)`);
      }

      if (successCount > 0) {
        onClose();
      }
    } catch (e) {
      console.error(e);
      capture(e);
      toast.error(t("error_sending_message"));
    } finally {
      setSending(false);
    }
  };

  if (candidates.length === 0) return null;

  const recipientText =
    candidates.length === 1 ? `${candidates[0]?.firstname} ${candidates[0]?.lastname}` || candidates[0]?.email : t("sending_to_multiple").replace("{count}", candidates.length);

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="max-w-2xl w-full">
      <div className="pt-8">
        {/* Header */}
        <div className="px-6 pb-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">{t("message_candidate")}</h2>
          <p className="text-sm text-gray-500 mt-1">To: {recipientText}</p>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t("message_subject")}</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter subject..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-shadow"
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t("message_body")}</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your message here..."
              rows={8}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-shadow resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            disabled={sending}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t("cancel")}
          </button>
          <LoadingButton
            onClick={handleSend}
            loading={sending}
            className="gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <FiMail className="w-4 h-4" />
            <span>{t("send_message")}</span>
          </LoadingButton>
        </div>
      </div>
    </Modal>
  );
}

const translations = {
  en: {
    title: "Talent Pool",
    search_placeholder: "Search candidates...",
    filter_status: "Status",
    filter_city_placeholder: "Filter by city",
    more_filters: "More Filters",
    all_statuses: "All statuses",
    all_jobs: "All jobs",
    total_candidates: "Total candidates:",
    selected: "selected",
    select_all: "Select all",
    select_all_candidates: "Select all {count} candidates",
    all_candidates_selected: "All {count} candidates selected",
    clear: "Clear",
    export: "Export",
    export_success: "Export successful",
    export_error: "Error exporting candidates",
    candidate_name: "Name",
    job_applied: "Job title",
    city: "City",
    country: "Country",
    last_company: "Last Company",
    last_school: "Last School",
    applied_date: "Applied Date",
    screening_score: "Score",
    no_candidates: "No candidates found",
    no_candidates_description: "No candidates have applied yet",
    fetch_error: "Error fetching candidates",
    error_fetching_candidate: "Error fetching candidate details",
    status_active: "New",
    status_applied: "New",
    status_preselected: "Preselected",
    status_interview: "Interview",
    status_selected: "Recruited",
    status_refused: "Rejected",
    red_flags: "Critical Issues",
    yellow_flags: "Points to Investigate",
    social: "Social",
    filter_job: "Job",
    import_candidate: "Import Candidate",
    message_candidate: "Send Message",
    message_subject: "Subject",
    message_body: "Message",
    send_message: "Send",
    cancel: "Cancel",
    message_sent: "Message sent successfully",
    messages_sent: "Messages sent successfully to {count} candidates",
    error_sending_message: "Error sending message",
    subject_required: "Subject is required",
    message_required: "Message is required",
    sending_to_multiple: "Sending to {count} candidates",
  },
  fr: {
    title: "Vivier",
    search_placeholder: "Rechercher des candidats...",
    filter_status: "Statut",
    filter_city_placeholder: "Filtrer par ville",
    more_filters: "Plus de filtres",
    all_statuses: "Tous les statuts",
    all_jobs: "Toutes les offres",
    total_candidates: "Total candidats:",
    selected: "sélectionné(s)",
    select_all: "Tout sélectionner",
    select_all_candidates: "Sélectionner les {count} candidats",
    all_candidates_selected: "Tous les {count} candidats sont sélectionnés",
    clear: "Effacer",
    export: "Exporter",
    export_success: "Export réussi",
    export_error: "Erreur lors de l'export des candidats",
    candidate_name: "Nom",
    job_applied: "Titre du poste",
    city: "Ville",
    country: "Pays",
    last_company: "Dernière entreprise",
    last_school: "Dernière école",
    applied_date: "Date de candidature",
    screening_score: "Score",
    no_candidates: "Aucun candidat trouvé",
    no_candidates_description: "Aucun candidat n'a encore postulé",
    fetch_error: "Erreur lors de la récupération des candidats",
    error_fetching_candidate: "Erreur lors de la récupération des détails du candidat",
    status_active: "Nouveau",
    status_applied: "Nouveau",
    status_preselected: "Présélectionné",
    status_interview: "Entretien",
    status_selected: "Recruté",
    status_refused: "Rejeté",
    red_flags: "Problèmes critiques",
    yellow_flags: "Points à investiguer",
    social: "Réseaux sociaux",
    filter_job: "Offre",
    import_candidate: "Importer un candidat",
    message_candidate: "Envoyer un message",
    message_subject: "Sujet",
    message_body: "Message",
    send_message: "Envoyer",
    cancel: "Annuler",
    message_sent: "Message envoyé avec succès",
    messages_sent: "Messages envoyés avec succès à {count} candidats",
    error_sending_message: "Erreur lors de l'envoi du message",
    subject_required: "Le sujet est requis",
    message_required: "Le message est requis",
    sending_to_multiple: "Envoi à {count} candidats",
  },
};
